import { ArrowRight, FileCode2, Quote, RefreshCcw, ScanEye, Tags } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { routeToIntake } from '../lib/intakeRouting';

// Rebuilt per Visual Contract §10.5 + MVP Contract §5. Public name is "AEO Visibility
// Repair". Violet/cyan with the eye-texture background ("the eye that never closes").
// Promises only observable remediation — no AI ranking / inclusion / citation /
// revenue guarantees, no paid placement, no algorithmic manipulation.
const remediations: Array<{ title: string; detail: string; icon: LucideIcon }> = [
  {
    title: 'Clearer entity facts',
    detail: 'Make who you are, what you do, and where you operate unambiguous to machines.',
    icon: ScanEye,
  },
  {
    title: 'Structured evidence & schema',
    detail: 'Add the structured data and proof signals answer engines look for.',
    icon: Tags,
  },
  {
    title: 'LLM context improvements',
    detail: 'Publish a machine-readable context record describing your services and proof.',
    icon: FileCode2,
  },
  {
    title: 'Citation-basis repair',
    detail: 'Strengthen the verifiable basis behind your claims so they can be cited.',
    icon: Quote,
  },
  {
    title: 'Findable re-scan comparison',
    detail: 'Where available, compare a Findable re-scan to evidence the repair signal.',
    icon: RefreshCcw,
  },
];

export function AeoRepair() {
  return (
    <section id="aeo" className="relative w-full overflow-hidden bg-obsidian py-24 sm:py-28" data-aeo-section="aeo-visibility-repair">
      {/* Eye-texture background — the ratified eye motif, "the eye that never closes" */}
      <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
        <img
          src="/assets/storyboard_s1_hero_v2.webp"
          alt=""
          className="h-full w-full object-cover opacity-[0.12]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-obsidian/80 to-obsidian" />
      </div>

      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-sm font-black uppercase tracking-wide text-cyan">AEO Visibility Repair</p>
          <h2 className="mt-4 text-4xl font-black leading-tight tracking-tight text-chalk sm:text-5xl">
            If AI can't see you, do you even exist?
          </h2>
          <p className="mt-6 text-lg leading-8 text-ash">
            AI answer engines now sit between you and your customers. If AI systems cannot read,
            understand, or cite your business, you are invisible in the AI-mediated discovery layer.
          </p>
          <p className="mt-4 text-base leading-7 text-ash/80">
            AEO Visibility Repair fixes the machine-readable evidence layer behind your business.
            (AEO is short for Agentic Engine Optimisation — the principle that a business must be
            machine-readable, structured, and citable.)
          </p>
        </div>

        <div className="mx-auto mt-14 grid max-w-5xl gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {remediations.map((item) => {
            const Icon = item.icon;
            return (
              <article key={item.title} className="border border-violet/25 bg-white/[0.04] p-6">
                <Icon className="size-7 text-violet" aria-hidden="true" />
                <h3 className="mt-5 text-lg font-black text-chalk">{item.title}</h3>
                <p className="mt-3 text-sm leading-6 text-ash">{item.detail}</p>
              </article>
            );
          })}
        </div>

        <p className="mx-auto mt-10 max-w-3xl text-center text-sm leading-6 text-ash/80">
          AEO Visibility Repair is observable remediation, not a ranking, inclusion, citation, or
          revenue guarantee. Macellum improves the evidence; it never promises what an answer engine
          will do with it.
        </p>

        <div className="mt-10 flex justify-center">
          <button
            type="button"
            onClick={() => routeToIntake({ intent: 'aeo', productHint: 'aeo_t1', sourceId: 'findable' })}
            className="inline-flex h-12 items-center justify-center gap-2 border border-cyan px-6 text-sm font-black uppercase tracking-wide text-cyan transition hover:bg-cyan hover:text-obsidian"
          >
            Repair my AI visibility
            <ArrowRight className="size-5" aria-hidden="true" />
          </button>
        </div>
      </div>
    </section>
  );
}
